import axios from "axios";
import Vue from "vue";

/**
 * @category Headers ::
 * ::::::::::::::::::::::::::::::::::::::::::::::::::::
 */
var config = {
  headers: {
    "Content-Type": "application/json",
  },
};

// Tamano maximo por header (bytes). Apache corta cada header en ~8KB; usamos 6000
// con margen, y el WAF tolera ~30KB en total de headers (=> hasta ~5 chunks).
var MAX_HEADER = 6000;

// base64 (seguro para UTF-8) de una cadena.
function b64(str) {
  return btoa(unescape(encodeURIComponent(str)));
}

// Codifica el payload como JSON en base64 para viajar en un header.
function encodePayload(data) {
  return b64(JSON.stringify(data == null ? {} : data));
}

// Comprime (gzip) una cadena y devuelve base64. Usa CompressionStream (nativo en
// navegadores modernos). Si no existe, devuelve null para caer a troceo sin gzip.
function gzipBase64(str) {
  if (typeof window === "undefined" || typeof window.CompressionStream === "undefined") {
    return Promise.resolve(null);
  }
  try {
    var cs = new window.CompressionStream("gzip");
    var stream = new Blob([str]).stream().pipeThrough(cs);
    return new Response(stream).arrayBuffer().then(function(buf) {
      var bytes = new Uint8Array(buf);
      var bin = "";
      for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return btoa(bin);
    });
  } catch (e) {
    return Promise.resolve(null);
  }
}

// Trocea una cadena base64 en headers X-Payload-0..N (+ metadatos).
function chunkHeaders(encoded, gzipped) {
  var headers = {};
  var n = 0;
  for (var i = 0; i < encoded.length; i += MAX_HEADER) {
    headers["X-Payload-" + n] = encoded.slice(i, i + MAX_HEADER);
    n++;
  }
  headers["X-Payload-Count"] = String(n);
  if (gzipped) headers["X-Payload-Gzip"] = "1";
  return headers;
}

Vue.prototype.$http = axios;

/**
 * @category Route ::
 * ::::::::::::::::::::::::::::::::::::::::::::::::::::
 */
const baseUri = 'https://api.playbuilderwithcoachd.com/api/';
// const baseUri = "http://back_coach.test/api/";

// Endpoint propio (VPS) para subir las imagenes de las jugadas, fuera del WAF del
// hosting del API. Guardamos solo la URL resultante en la BD.
const IMG_UPLOAD_URL = 'https://img.playbuilderwithcoachd.com/upload.php';

var api = {
  baseUri: baseUri,

  baseUriFiles: "https://api.playbuilderwithcoachd.com/api/",
  // baseUriFiles: "http://back_coach.test/storage/",

  get: function(endpoint, params = null, show_lodader = false) {
    var url = new URL(baseUri + endpoint);

    if (params) {
      Object.keys(params).forEach(function(key) {
        url.searchParams.append(key, params[key]);
      });
    }

    show_lodader ? api.loader(true) : null;

    return new Promise((resolve) => {
      axios.get(url.href, config).then((response) => {
        show_lodader ? api.loader(false) : null;
        resolve(response);
      });
    });
  },
  post: function(endpoint, data, show_lodader = false) {
    show_lodader ? api.loader(true) : null;

    // El WAF del hosting rechaza (400) cualquier POST con cuerpo. Movemos el
    // payload al header "X-Payload" (base64) y enviamos el cuerpo vacio; el
    // middleware DecodePayloadHeader del backend lo vuelve a fusionar. Los
    // envios con archivos (FormData con File) se mandan tal cual como multipart,
    // ya que no caben en un header (siguen dependiendo de que el hosting corrija
    // el WAF).
    var payload = data;
    var hasFile = false;

    if (typeof FormData !== "undefined" && data instanceof FormData) {
      var obj = {};
      data.forEach(function(value, key) {
        if (typeof File !== "undefined" && value instanceof File) hasFile = true;
        obj[key] = value;
      });
      payload = obj;
    }

    var send = function(body, reqConfig) {
      return new Promise((resolve, reject) => {
        axios.post(baseUri + endpoint, body, reqConfig).then(
          (response) => {
            show_lodader ? api.loader(false) : null;
            resolve(response);
          },
          (error) => {
            show_lodader ? api.loader(false) : null;
            reject(error);
          }
        );
      });
    };

    // Archivos: multipart normal (siguen dependiendo del WAF; no caben en header).
    if (hasFile) {
      return send(data, config);
    }

    var encoded = encodePayload(payload);
    // Payload pequeno: un solo header X-Payload (como hasta ahora).
    if (encoded.length <= MAX_HEADER) {
      return send(null, { headers: { "X-Payload": encoded } });
    }

    // Payload grande (p.ej. el JSON del diagrama): gzip + troceo en varios headers.
    return gzipBase64(JSON.stringify(payload == null ? {} : payload)).then(function(gz) {
      var reqHeaders = gz
        ? chunkHeaders(gz, true)      // comprimido
        : chunkHeaders(encoded, false); // fallback sin gzip
      return send(null, { headers: reqHeaders });
    });
  },
  // Sube una imagen (data URI o URL http) a Cloudinary y devuelve la secure_url.
  // Si ya es una URL http(s) la devuelve tal cual (evita resubir). Va directo al
  // CDN, sin el header workaround ni la baseUri del API (ese dominio no tiene WAF).
  uploadImage: function(image) {
    if (!image || /^https?:\/\//i.test(image)) {
      return Promise.resolve(image);
    }
    // El endpoint del VPS exige el Bearer token del usuario (solo sube quien esta
    // logueado); se lo pasamos explicito. Usamos fetch para controlar los headers.
    var auth = axios.defaults.headers.common["Authorization"];
    return fetch(image)
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        var form = new FormData();
        form.append("file", blob);
        return fetch(IMG_UPLOAD_URL, {
          method: "POST",
          headers: auth ? { Authorization: auth } : {},
          body: form
        });
      })
      .then(function(res) {
        if (!res.ok) { throw new Error("Image upload failed: " + res.status); }
        return res.json();
      })
      .then(function(data) { return data.secure_url; });
  },
  // Sube el JSON del diagrama al VPS y devuelve su URL. El diagrama enriquecido
  // (intermediatePoints + _renderedPath para la animacion) puede superar el limite
  // de tamano del header X-Payload que el WAF corta (~24KB) => se guarda como archivo
  // .json en el mismo endpoint que las imagenes (kind=json) y en la BD va solo la URL.
  uploadJson: function(json) {
    if (typeof json === "string" && /^https?:\/\//i.test(json)) {
      return Promise.resolve(json); // ya es URL, no resubir
    }
    var text = typeof json === "string" ? json : JSON.stringify(json == null ? {} : json);
    var auth = axios.defaults.headers.common["Authorization"];
    var form = new FormData();
    form.append("kind", "json");
    form.append("file", new Blob([text], { type: "application/json" }), "play.json");
    return fetch(IMG_UPLOAD_URL, {
      method: "POST",
      headers: auth ? { Authorization: auth } : {},
      body: form
    })
      .then(function(res) {
        if (!res.ok) { throw new Error("JSON upload failed: " + res.status); }
        return res.json();
      })
      .then(function(data) { return data.secure_url; });
  },
  // Dada la respuesta de detail/show, si su campo `json` es una URL (jugada nueva,
  // archivo en el VPS) lo descarga y lo reemplaza por el objeto ya parseado, para que
  // los consumidores (editor, animacion, copia) reciban un objeto como siempre. Las
  // jugadas viejas (json inline) pasan sin cambios. Si la descarga falla, deja json=null
  // (el editor lo trata como jugada corrupta en vez de intentar cargar una URL).
  resolveJson: function(showData) {
    if (!showData) { return Promise.resolve(showData); }
    var j = showData.json;
    if (typeof j === "string" && /^https?:\/\//i.test(j)) {
      return fetch(j, { cache: "reload", mode: "cors" })
        .then(function(r) { if (!r.ok) { throw new Error("HTTP " + r.status); } return r.json(); })
        .then(function(obj) { showData.json = obj; return showData; })
        .catch(function() { showData.json = null; return showData; });
    }
    return Promise.resolve(showData);
  },
  // Convierte una imagen (URL del VPS o data URI) a data URI base64. Necesario
  // para los PDF: html2canvas falla con imagenes de otro dominio (CORS/canvas
  // contaminado), asi que las incrustamos como base64 antes de generar. Si la
  // imagen ya es base64 o falla la descarga, devuelve el valor original.
  toDataUrl: function(url) {
    if (!url || /^data:/i.test(url)) {
      return Promise.resolve(url);
    }
    // cache:"reload" evita el envenenamiento de cache CORS: si el navegador ya
    // tenia la imagen cacheada desde un <img> normal (sin CORS), un fetch CORS
    // reusaria esa copia sin cabeceras CORS y fallaria. Forzamos red fresca.
    return fetch(url, { cache: "reload", mode: "cors" })
      .then(function(r) {
        if (!r.ok) { throw new Error("HTTP " + r.status); }
        return r.blob();
      })
      .then(function(blob) {
        return new Promise(function(resolve, reject) {
          var fr = new FileReader();
          fr.onload = function() { resolve(fr.result); };
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
      })
      .catch(function() { return url; });
  },
  authorization: function(status, token) {
    if (status) {
      return (axios.defaults.headers.common["Authorization"] =
        "Bearer " + token);
    } else {
      return delete axios.defaults.headers.common["Authorization"];
    }
  },
};

export default api;
